// apps/landing-page/src/components/search/SearchDialog.tsx
// Modal search below `xl` (full-screen sheet on phones, centred card from `sm`). From `xl`
// the navbar has an inline combobox instead (SearchCombobox).
'use client';

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { CatalogSearch } from './useCatalogSearch';
import { useSearchBox } from './useSearchBox';
import { SearchResults } from './SearchResults';

const FOCUSABLE = 'a[href], button:not([disabled]), input';
// Tailwind `xl`. Media queries read rem against the browser default, not the 14px `html`.
const XL_QUERY = '(min-width: 80rem)';

interface SearchDialogProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  search: CatalogSearch;
}

export function SearchDialog({ open, onOpen, onClose, search }: SearchDialogProps) {
  // Ctrl+K / Cmd+K from anywhere: at xl it focuses the navbar input, below it opens the dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      if (window.matchMedia(XL_QUERY).matches) {
        document.querySelector<HTMLInputElement>('[data-search-input]')?.focus();
      } else if (!open) {
        onOpen();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpen]);

  return open ? <DialogBody onClose={onClose} search={search} /> : null;
}

// Mounted only while open, so the query and active row start fresh every time.
function DialogBody({ onClose, search }: { onClose: () => void; search: CatalogSearch }) {
  const t = useTranslations('search');
  const titleId = useId();
  const listId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const box = useSearchBox(search, listId, onClose);
  const showList = box.rows.length > 0;
  const { ensureLoaded } = search;

  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  // Body scroll lock, input focus, and focus back to the opener on close.
  useEffect(() => {
    const active = document.activeElement as HTMLElement | null;
    const opener = active && active !== document.body ? active : null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = originalOverflow;
      const trigger = opener?.isConnected
        ? opener
        : Array.from(document.querySelectorAll<HTMLElement>('[data-search-trigger]')).find((el) => el.offsetParent !== null);
      trigger?.focus();
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    } else if (e.key === 'Tab') {
      // Keep Tab inside the dialog (options are tabIndex -1, so this cycles input, buttons, links).
      const focusables = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter((el) => el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    } else if (e.target === inputRef.current) {
      box.onKeyDown(e);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] sm:flex sm:items-start sm:justify-center sm:px-4 sm:pt-[12vh]" onKeyDown={handleKeyDown}>
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-slate-900/40" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Keep focus inside: a press on padding or the skeleton would otherwise drop
        // focus to <body>, and Escape and the Tab trap both listen from within.
        onMouseDown={(e) => {
          if (!(e.target as HTMLElement).closest('a, button, input')) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
        className="relative flex h-dvh w-full flex-col bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-2xl sm:h-auto sm:max-h-[70vh] sm:max-w-xl sm:rounded-2xl sm:pt-0 sm:pb-0"
      >
        <h2 id={titleId} className="sr-only">{t('dialogTitle')}</h2>

        {/* Phones: back button first, then the input. From sm: input, then a close button on the right. */}
        <div className="flex items-center gap-2 border-b-2 border-slate-200 px-4 focus-within:border-teal-600">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="-ml-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 sm:order-last sm:ml-0 sm:h-10 sm:w-10 sm:text-slate-500"
          >
            <ArrowLeft className="h-5 w-5 sm:hidden" />
            <X className="hidden h-5 w-5 sm:block" />
          </button>
          <Search aria-hidden="true" className="hidden h-5 w-5 flex-shrink-0 text-slate-400 sm:block" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={t('inputLabel')}
            aria-expanded={showList}
            aria-controls={showList ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={box.activeDomId}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('placeholder')}
            value={box.query}
            onChange={(e) => box.setQuery(e.target.value)}
            className="h-14 min-w-0 flex-1 bg-transparent text-[16px] text-slate-900 placeholder:text-slate-400 outline-none!"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2 sm:p-3">
          <SearchResults search={search} box={box} listId={listId} onSelect={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
